/**
 * Donut Chart — for composition breakdowns (department mix, status distribution).
 *
 * Usage:
 *   <DonutChart
 *     data={[
 *       { label: "Resolved", value: 120, color: "#10B981" },
 *       { label: "Pending", value: 42, color: "#F59E0B" },
 *     ]}
 *     centerLabel="162"
 *     centerSubLabel="Total"
 *   />
 */

import React from 'react';
import {
  ResponsiveContainer, PieChart, Pie, Cell, Tooltip,
} from 'recharts';
import { CHART_COLORS, RECHARTS_DEFAULTS, formatMetricValue, CHART_FONTS } from '../../config/chartTheme';

interface DonutDataPoint {
  label: string;
  value: number;
  color?: string;
}

interface DonutChartProps {
  data: DonutDataPoint[];
  height?: number;
  unit?: string;
  centerLabel?: string;
  centerSubLabel?: string;
  innerRadius?: number;
  outerRadius?: number;
}

const CustomTooltip = ({ active, payload, unit }: any) => {
  if (!active || !payload?.length) return null;
  const d = payload[0];
  return (
    <div style={RECHARTS_DEFAULTS.tooltipContentStyle}>
      <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
        <div style={{
          width: 8, height: 8, borderRadius: '50%',
          backgroundColor: d.payload?.fill || d.color,
        }} />
        <span style={{ color: CHART_COLORS.textSecondary, fontSize: '11px' }}>{d.name}</span>
      </div>
      <div style={{ color: CHART_COLORS.textPrimary, fontWeight: 600, marginTop: '2px' }}>
        {formatMetricValue(d.value, unit || 'COUNT')}
      </div>
    </div>
  );
};

export const DonutChart: React.FC<DonutChartProps> = ({
  data, height = 200, unit = 'COUNT',
  centerLabel, centerSubLabel,
  innerRadius = 55, outerRadius = 80,
}) => {
  return (
    <div style={{ position: 'relative', width: '100%', height }}>
      <ResponsiveContainer width="100%" height={height}>
        <PieChart>
          <Pie
            data={data}
            dataKey="value"
            nameKey="label"
            cx="50%"
            cy="50%"
            innerRadius={innerRadius}
            outerRadius={outerRadius}
            paddingAngle={2}
            strokeWidth={0}
          >
            {data.map((d, i) => (
              <Cell
                key={i}
                fill={d.color || CHART_COLORS.series[i % CHART_COLORS.series.length]}
              />
            ))}
          </Pie>
          <Tooltip content={<CustomTooltip unit={unit} />} />
        </PieChart>
      </ResponsiveContainer>

      {/* Center label */}
      {centerLabel && (
        <div style={{
          position: 'absolute',
          top: '50%',
          left: '50%',
          transform: 'translate(-50%, -50%)',
          textAlign: 'center',
          pointerEvents: 'none',
        }}>
          <div style={{
            fontFamily: CHART_FONTS.mono,
            fontSize: '20px',
            fontWeight: 600,
            color: CHART_COLORS.textPrimary,
            lineHeight: 1.2,
          }}>
            {centerLabel}
          </div>
          {centerSubLabel && (
            <div style={{
              fontSize: '10px',
              textTransform: 'uppercase',
              letterSpacing: '0.6px',
              color: CHART_COLORS.textMuted,
              marginTop: '2px',
            }}>
              {centerSubLabel}
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default DonutChart;
